const getFileExtension = (fileName) => {
    if (!fileName) {
        return false
    }
    return fileName.split('.').pop()
}

/**
 * 格式化字节大小
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
const formatFileSize = (bytes, si = false, dp = 1) => {
    const thresh = si ? 1000 : 1024
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
    }
    const units = si ? ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
    let u = -1
    const r = 10 ** dp
    do {
        bytes /= thresh
        ++u
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)
    return bytes.toFixed(dp) + ' ' + units[u]
}

/**
 * 随机uuid
 * @param {*} len
 * @param {*} radix 基数
 * @returns
 */
const randomUUID = (len, radix) => {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('')
    let uuid = [],
        i
    radix = radix || chars.length

    if (len) {
        // Compact form
        for (i = 0; i < len; i++) uuid[i] = chars[0 | (Math.random() * radix)]
    } else {
        // rfc4122, version 4 form
        let r
        // rfc4122 requires these characters
        uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-'
        uuid[14] = '4'

        // Fill in random data.  At i==19 set the high bits of clock sequence as
        // per rfc4122, sec. 4.1.5
        for (i = 0; i < 36; i++) {
            if (!uuid[i]) {
                r = 0 | (Math.random() * 16)
                uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r]
            }
        }
    }
    return uuid.join('')
}

/**
 * 获取文件base64
 * @param {*} raw
 */
const getBase64 = (raw) => {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        let base64URL = ''
        fileReader.readAsDataURL(raw)
        fileReader.onload = (e) => {
            base64URL = e.target.result
        }
        fileReader.onerror = () => {
            reject(new Error('文件流异常'))
        }
        fileReader.onloadend = () => {
            resolve(base64URL)
        }
    })
}

/**
 * blob url转file
 * 暂时只能兼容image
 * @param {*} blob
 * @returns File
 */
const blobUrlToFile = async (blobUrl) => {
    const blob = await fetch(blobUrl).then((res) => res.blob())
    const fileName = blob.type.split('/').pop()
    const file = new File([blob], `${new Date().getTime()}.${fileName}`, { type: blob.type })
    return file
}

const formatNumber = ({ num = 0, point = 1, suffix = 'w', digits = 5 } = {}) => {
    let numStr = num.toString()
    if (numStr.length < digits) {
        // 1w以内直接返回
        return numStr
    } else if (numStr.length >= digits) {
        // 转成w
        let decimal = numStr.substring(numStr.length - 4, numStr.length - 4 + point)
        return parseInt(num / 10000) + '.' + decimal + suffix
    }
}

// 检测编辑器内容是否为空
const checkEditorVal = (val) => {
    let num = 0
    const valReg = /<p>+(\s+|$nbsp;|&nbsp;|\s+)+<\/p>|<p>(<br>)+<\/p>/g

    while (num < val.length && val != '') {
        num++
        const k = val.match(valReg)
        if (k) {
            val = val.replace(k[0], '')
        }
    }
    return val == ''
}

const getDateStr = (date) => {
    var year = date.getFullYear()
    var month = date.getMonth() + 1
    var day = date.getDate()
    month = month > 9 ? month : '0' + month
    day = day > 9 ? day : '0' + day
    return year + '-' + month + '-' + day
}

/**
 * 复制文本
 * @param {*} text 文本
 * @returns
 */
const copyText = (text) => {
    return new Promise((resolve, reject) => {
        if (document.execCommand('copy')) {
            const textarea = document.createElement('textarea')
            textarea.value = text
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
            resolve({ status: 'success' })
        } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(text).then(() => {
                resolve({ status: 'success' })
            })
        } else {
            reject(new Error('复制文本失败！'))
        }
    })
}

/**
 * 复制指定元素的格式化文本到剪贴板
 * @param {Element} dom 要复制的 dom 元素
 * @returns
 */
const copyFormattedTextToClipboard = (dom) => {
    return new Promise((resolve, reject) => {
        const range = document.createRange()
        range.selectNodeContents(dom)
        const selection = window.getSelection()
        selection.removeAllRanges()
        selection.addRange(range)
        if (document.execCommand('copy')) {
            document.execCommand('copy')
            selection.removeAllRanges()
            resolve({ status: 'success' })
        } else if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard
                .writeText(dom.textContent)
                .then(() => {
                    resolve({ status: 'success' })
                })
                .catch((error) => {
                    reject(new Error(`复制文本失败: ${error.message}`))
                })
        } else {
            reject(new Error('复制文本失败！'))
        }
    })
}

/**
 * 更新指定的下拉选择列表
 *
 * @param {Function} apiFunction - 用于获取数据的 API 函数
 * @param {Object} params - 传递给 API 函数的参数对象
 * @param {Array} selectFormList - 选择表单项的列表
 * @param {String} key - 用于查找选择表单项的键名
 * @param {Object} options - 用于自定义返回数据的键名和其他配置。
 * @param {String} options.titleKeyName - 映射为列表中 `title` 字段的键名，默认值为 'nameCn'
 * @param {String} options.valueKeyName - 映射为列表中 `value` 字段的键名，默认值为 'id'
 * @param {Boolean} options.includeAllOption - 是否在列表中添加一个 "所有" 选项，默认值为 true。
 * @param {String} options.allOptionTitle - "所有" 选项的标题，默认值为 '所有'
 * @returns {Boolean}
 */
async function fetchAndSetDataList(apiFunction, params, selectFormList, key, options = {}) {
    const { titleKeyName = 'nameCn', valueKeyName = 'id', includeAllOption = true, allOptionTitle = '所有' } = options

    try {
        const res = await apiFunction(params)
        if (res.code !== 200) return false

        let dataList = res.data.map((item) => ({
            title: item[titleKeyName],
            value: valueKeyName == 'dictOptionCode' ? +item[valueKeyName] : item[valueKeyName]
        }))

        if (includeAllOption && dataList.length > 0) {
            dataList.unshift({
                title: allOptionTitle,
                value: null
            })
        }

        const result = selectFormList.find((item) => item.key == key)
        if (!result) return false

        result.typeList = dataList
        if (dataList.length === 0) {
            result.value = null
        }

        return true
    } catch (error) {
        console.error(error)
        return false
    }
}

function getImageSize(url) {
    return new Promise((resolve) => {
        const img = new Image()
        img.src = url
        if (img.complete) {
            resolve({ width: img.width, height: img.height })
        } else {
            img.onload = () => {
                resolve({ width: img.width, height: img.height })
            }
        }
    })
}

/**
 * 验证金额格式（整数不超过8位，小数不超过2位）
 * @param {string} value - 要验证的金额字符串
 * @returns {boolean} - 如果金额格式正确则返回 true，否则返回 false
 */
function validateAmount(rule, value, callback) {
    if (!value) {
        callback()
        return
    }

    // 确保value是字符串类型
    const valueStr = String(value)

    // 移除可能的千分位逗号
    const cleanValue = valueStr.replace(/,/g, '')

    // 检查是否包含非数字字符（除了小数点）
    if (!/^\d*\.?\d*$/.test(cleanValue)) {
        callback(new Error('只能输入数字和小数点'))
        return
    }

    // 检查多个小数点
    if ((cleanValue.match(/\./g) || []).length > 1) {
        callback(new Error('只能有一个小数点'))
        return
    }

    // 检查以小数点开头
    if (cleanValue.startsWith('.')) {
        callback(new Error('不能以小数点开头'))
        return
    }

    const parts = cleanValue.split('.')
    const integerPart = parts[0]
    const decimalPart = parts[1] || ''

    // 检查整数部分长度
    if (integerPart.length > 8) {
        callback(new Error('整数部分不能超过8位'))
        return
    }

    // 检查小数部分长度
    if (decimalPart.length > 2) {
        callback(new Error('小数部分不能超过2位'))
        return
    }

    // 检查整数部分不能以0开头（除非是0本身）
    if (integerPart.length > 1 && integerPart.startsWith('0')) {
        callback(new Error('整数部分不能以0开头'))
        return
    }

    callback()
}

/**
 * 验证表单空值，将空字符串转换为 null
 * @param {Object} obj - 要验证的表单对象
 * @returns {Object} - 验证后的表单对象
 */

function validateForm(obj) {
    Object.keys(obj).forEach((key) => {
        if (obj[key] === '') {
            obj[key] = null
        }
    })
    return obj
}

module.exports = {
    getFileExtension,
    formatFileSize,
    randomUUID,
    getBase64,
    blobUrlToFile,
    formatNumber,
    checkEditorVal,
    getDateStr,
    copyText,
    copyFormattedTextToClipboard,
    fetchAndSetDataList,
    getImageSize,
    validateAmount,
    validateForm
}
